import { timestampDate } from "@bufbuild/protobuf/wkt";
import dayjs from "dayjs";
import { countBy } from "lodash-es";
import { useMemo } from "react";
import { useMemos } from "@/hooks/useJournalQueries";
import { useTagCounts, useTickerCounts } from "@/hooks/useUserQueries";
import type { StatisticsData } from "@/types/statistics";

export interface FilteredMemoStats {
  statistics: StatisticsData;
  tags: Record<string, number>;
  tickers: Record<string, number>;
  loading: boolean;
}

export interface UseFilteredMemoStatsOptions {
  userName?: string;
}

export const useFilteredMemoStats = (options: UseFilteredMemoStatsOptions = {}): FilteredMemoStats => {
  const { userName } = options;

  // Fetch tag counts with display names
  // userName exists: fetch specific user stats (Home/Profile)
  // userName undefined: Explore page - temporarily empty (see issue #83)
  const { data: tagCountsData, isLoading: isLoadingTagCounts } = useTagCounts(!!userName);

  // Fetch ticker counts (from backend)
  // userName exists: fetch specific user stats (Home/Profile)
  // userName undefined: Explore page - temporarily empty (see issue #83)
  const { data: tickerCountMap, isLoading: isLoadingTickerCounts } = useTickerCounts(!!userName);

  // NOTE: 调用 useMemos({}) 用于 fallback 计算（当 userStats 不可用时）
  // 这会导致 createMemo 后 ListMemos 被调用 2 次（一次来自 PagedMemoList，一次来自这里）
  //
  // 原因：
  // - PagedMemoList 使用带过滤条件的参数: { state, orderBy, filter, pageSize, includeAiAnalysis }
  // - 这里使用空对象参数: {} 用于获取所有数据计算统计
  // - createMemo 成功后 invalidateQueries 会使两者都重新获取
  //
  // 这是预期行为：
  // - 生产环境启用缓存后不会有此问题（staleTime 内会复用缓存）
  // - 两个组件有不同职责：PagedMemoList 显示分页列表，这里计算全局统计
  //
  // 相关问题：https://github.com/xinghui/valuation/issues/67
  const { data: memosResponse, isLoading: isLoadingMemos } = useMemos({});

  const data = useMemo(() => {
    const loading = isLoadingTagCounts || isLoadingTickerCounts || isLoadingMemos;
    let activityStats: Record<string, number> = {};
    let tagCount: Record<string, number> = {};
    let tickerCount: Record<string, number> = {};

    // Use tagId->count for TagsSection (always)
    // tagCountsData is Record<string, number> (tagId → count)
    const hasBackendTagStats = tagCountsData && Object.keys(tagCountsData).length > 0;
    if (hasBackendTagStats) {
      tagCount = tagCountsData as Record<string, number>;
    }

    // Note: displayNameCount is not available from current API
    // We'll compute activity stats from memos instead

    // Compute from memos for activity statistics and fallback cases
    if (memosResponse?.memos) {
      const displayTimeList: Date[] = [];
      const memos = memosResponse.memos;

      // Only compute from memos if backend stats are not available
      const needFallback = !hasBackendTagStats || !tickerCountMap || Object.keys(tickerCountMap).length === 0;

      if (needFallback) {
        for (const memo of memos) {
          // Collect display timestamps for activity calendar
          const displayTime = memo.displayTime ? timestampDate(memo.displayTime) : undefined;
          if (displayTime) {
            displayTimeList.push(displayTime);
          }

          // Count tags (fallback when backend stats not available)
          if (!hasBackendTagStats && memo.tags && memo.tags.length > 0) {
            for (const tag of memo.tags) {
              tagCount[tag] = (tagCount[tag] || 0) + 1;
            }
          }

          // Count tickers from memos (for fallback only, prefer backend stats)
          if (!tickerCountMap || Object.keys(tickerCountMap).length === 0) {
            const memoWithTickers = memo as unknown as { tickers?: string[] };
            if (Array.isArray(memoWithTickers.tickers) && memoWithTickers.tickers.length > 0) {
              for (const ticker of memoWithTickers.tickers) {
                tickerCount[ticker] = (tickerCount[ticker] || 0) + 1;
              }
            }
          }
        }
      } else {
        // Only collect display timestamps when backend stats are available
        for (const memo of memos) {
          const displayTime = memo.displayTime ? timestampDate(memo.displayTime) : undefined;
          if (displayTime) {
            displayTimeList.push(displayTime);
          }
        }
      }

      // Use computed activity stats if backend stats not available
      if (Object.keys(activityStats).length === 0) {
        activityStats = countBy(displayTimeList.map((date) => dayjs(date).format("YYYY-MM-DD")));
      }
    }

    // Use ticker counts from backend if available
    if (tickerCountMap && Object.keys(tickerCountMap).length > 0) {
      tickerCount = tickerCountMap;
    }

    return {
      statistics: { activityStats },
      tags: tagCount,
      tickers: tickerCount,
      loading,
    };
  }, [userName, tagCountsData, tickerCountMap, memosResponse, isLoadingTagCounts, isLoadingTickerCounts, isLoadingMemos]);

  return data;
};
